import { z } from 'zod'
import { isValidEmail } from '../validation/email'

const VAT_ID_REGEX = /^[A-Z]{2}[0-9A-Z+*.]{2,13}$/
const COUNTRY_REGEX = /^[A-Z]{2}$/

/** A VAT number (UID) as it is stored: without spaces, upper-cased, null when empty. */
export function normalizeVatId(value: string | null | undefined): string | null {
  return value?.replace(/\s+/g, '').toUpperCase() || null
}

/** Whether a normalized VAT number has a country prefix and a plausible length; null passes. */
export function isVatId(value: string | null): boolean {
  return value === null || VAT_ID_REGEX.test(value)
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value ? value : null))

/** Company details a profile's invoices go to. */
export const billingEntityInputSchema = z.object({
  legalName: z.string().trim().min(1).max(255),
  street: optionalText(200),
  postalCode: optionalText(20),
  city: optionalText(100),
  country: optionalText(2)
    .transform((value) => value?.toUpperCase() ?? null)
    .refine((value) => value === null || COUNTRY_REGEX.test(value), 'Invalid country code'),
  vatId: z.string().nullish().transform(normalizeVatId).refine(isVatId, 'Invalid VAT number'),
  registrationNumber: optionalText(100),
  email: optionalText(255).refine(
    (value) => value === null || isValidEmail(value),
    'Invalid email address',
  ),
})
export type BillingEntityInput = z.infer<typeof billingEntityInputSchema>

export const billingEntitySchema = z.object({
  id: z.string().uuid(),
  isDefault: z.boolean(),
  legalName: z.string(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  vatId: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  email: z.string().nullable(),
})
export type BillingEntity = z.infer<typeof billingEntitySchema>

export const billingEntitiesResponseSchema = z.object({
  enabled: z.boolean(),
  entities: z.array(billingEntitySchema),
})
export type BillingEntitiesResponse = z.infer<typeof billingEntitiesResponseSchema>
